// npm dependencies
import React from 'react';
import i18next from 'i18next';
// data dependencies
import UCMS from '../../../utils/ucms';
import PCSC from '../../../utils/pcsc';
import { errorFactory, declareError } from '../../../utils/errors';
import { fetchDiagnosis } from '../../../utils/diagnosis';
import { deriveUrl, sleep } from '../../../utils/generic';
import {
    ucmsConstants,
    webpcscNoService,
    apiPath,
    vscPlaceholder,
    whfbPlaceholder,
    atrCases
} from '../../../constants';
// visual dependencies
import DeviceDetectionWithData from './DeviceDetectionWithData';

/* *****************************************************************************************
  Warning: this file is a dragon. test carefully after any change.
    the main loop or engine of this component is the handlePcscOnChange function
    be careful while using state, with this component it is very easy
    for weird state changes to conflict or occur out of order
****************************************************************************************** */

const TAG = 'DeviceDetection'; // eslint-disable-line no-unused-vars
class DeviceDetection extends React.Component {
    state = {
        connected: false,
        interval: false,
        syncing: false,
        readers: {},
    };

    componentDidMount() {
        // Latches handleNotFound/connected's mgmt advance to fire once per mount;
        // reset here so Back-then-retry gets a working detection screen.
        this.settled = false;
        this.pcscOnChange();
    }

    componentWillUnmount() {
        if (this.state.interval) {
            this.state.interval.stop();
            this.setState({ ...this.state, interval: null });
        }
    }

    setDetectReaders = (added, modified, updatedReaders) => {
        // eslint-disable-next-line max-len, no-return-assign, no-param-reassign
        added.forEach(
            (reader) =>
                (modified[reader] =
                    modified[reader] === undefined ? null : modified[reader]),
        );
        const modifiedReaders = {};
        Object.keys(modified).forEach((reader) => {
            modifiedReaders[reader] = {
                ...[reader],
                issuable: i18next.t(atrCases[modified[reader]]) || true,
                atr: modified[reader],
                readerName: reader,
                label: reader,
                changed: true,
            };
        });
        // React 18 automatic batching (createRoot) defers setState in setTimeout/promise
        // callbacks, so this.state is stale across the async detect->identify chain.
        // Thread the reader set explicitly so each step sees the accumulated readers, and
        // return the merged set for the next step. Mirrors VscCreationSelection/Scanner.
        const newReaders = {
            ...(updatedReaders || this.state.readers),
            ...modifiedReaders,
        };
        this.setState({
            ...this.state,
            readers: newReaders,
        });
        return newReaders;
    };

    pcscOnChange = async () => {
        try {
            await fetchDiagnosis(this.props.appDispatch);
            await PCSC.ready;
            const interval = await PCSC.onChange(
                this.handlePcscOnChange,
                true,
                1500,
            );
            this.setState({
                ...this.state,
                interval,
            });
        } catch (error) {
            console.error(
                TAG,
                'device selection identifyDevices error:',
                error,
            );
            console.error(TAG, 'PCSC.onChange:', error);
            declareError(
                this.props.appDispatch,
                errorFactory.err(`${webpcscNoService}_ERROR`),
            );
        }
    };

    handlePcscOnChange = async (added, removed, modified) => {
        this.setState({ ...this.state, syncing: true });
        let updatedReaders = { ...this.state.readers };
        if (removed.length > 0) {
            updatedReaders = this.removeReaders(removed, updatedReaders);
            // eslint-disable-next-line no-param-reassign
            removed.forEach((reader) => delete modified[reader]);
        }
        updatedReaders = this.setDetectReaders(added, modified, updatedReaders);
        const outcome = await this.identifyDevices(updatedReaders);
        this.applyDetectionOutcome(outcome);
        this.setState({
            ...this.state,
            syncing: false,
        });
    };

    // A VSC/WHfB credential can't be inserted by the user, so a sweep that misses
    // it must fail fast; a physical card keeps waiting for insertion (AC-3).
    applyDetectionOutcome = ({ match, candidatesSeen }) => {
        if (match) {
            this.connected(match);
            return;
        }
        if (this.settled) return; // already resolved this mount; don't re-fire
        const { subType } = this.props.device || {};
        const insertable =
            subType !== vscPlaceholder.subType &&
            subType !== whfbPlaceholder.subType;
        if (!insertable || candidatesSeen > 0) {
            this.settled = true;
            this.props.handleNotFound();
        }
    };

    removeReaders = (removed, updatedReaders) => {
        const readers = { ...(updatedReaders || this.state.readers) };

        this.updateDisplay(readers, this.disconnected);

        removed.forEach((readerName) => {
            delete readers[readerName];
        });
        this.setState({
            ...this.state,
            selected: removed.includes(this.state.selected)
                ? ''
                : this.state.selected,
            readers,
        });
        this.props.enrollingDispatch({
            type: 'SET_SELECTED_READER',
            reader: removed.includes(this.state.selected)
                ? null
                : readers[this.state.selected],
        });
        return readers;
    };

    // Connects to one reader and runs the APDU identify. Returns the identify result, or null on
    // error (the caller then skips that reader, exactly as the inline try/catch's `continue` did).
    // Extracted from identifyDevices so that function's cognitive complexity stays within bounds; the
    // connect -> atr-override -> apdu.run -> disconnect sequence and error handling are unchanged.
    runIdentify = async (readerName, readers) => {
        let reader = null;
        try {
            console.debug(TAG, 'trying 0:', readerName);
            reader = await PCSC.connect(readerName);
            var atr = readers[readerName].atr;
            // Hard coded the atr to 'axiad.whfb' for whfb
            if (readerName.includes('Windows Hello for Business')) {
                atr = 'axiad.whfb';
            }
            return await UCMS.apdu.run(
                reader,
                ucmsConstants.actions.identify,
                atr,
                () => {},
                deriveUrl(apiPath),
            );
        } catch (error) {
            console.error(TAG, 'identifyDevices:', error, readerName);
            return null;
        } finally {
            if (reader) await reader.disconnect();
        }
    };

    identifyDevices = async (updatedReaders) => {
        const readers = { ...(updatedReaders || this.state.readers) };
        await sleep(1000);
        let match = null;
        let candidatesSeen = 0;
        for (const readerName of Object.keys(readers)) {
            // eslint-disable-line no-restricted-syntax, max-len
            if (
                readers[readerName].changed === false ||
                readers[readerName].atr === null
            ) {
                continue; // eslint-disable-line no-continue
            }
            const result = await this.runIdentify(readerName, readers); // eslint-disable-line no-await-in-loop
            if (result === null) {
                continue; // eslint-disable-line no-continue
            }
            candidatesSeen += 1;
            const card = result.entries[0].result;
            console.debug(TAG, 'identifyDevices:card:', card);
            readers[readerName] = {
                ...readers[readerName],
                ...card,
                device: card,
            };
            if (this.matchDevice(readerName, readers)) {
                match = readerName;
                break;
            }
        }
        this.setState({
            ...this.state,
            readers: {
                ...this.state.readers,
                ...readers,
            },
        });
        if (typeof this.props.setReaderList === 'function') {
            this.props.setReaderList(readers);
        }
        return { readers, match, candidatesSeen };
    };

    connected = (readerName) => {
        this.props.setReader(readerName);
        this.setState({
            ...this.state,
            connected: true,
        });
        // props can't be read back reliably under React 18 automatic batching
        // (this.props.mgmt.detected was stale here); an instance field is not.
        // Relies on every caller's handleNotFound replacing the workflow, which
        // unmounts us and resets the latch; a caller that does not would strand
        // the wizard here after a not-found followed by a real match.
        if (!this.settled) {
            this.settled = true;
            this.props.mgmt.setDetected();
            setTimeout(() => {
                this.props.mgmt.next();
            }, 600);
        }
    };

    disconnected = () => {
        this.props.setReader('');
        this.setState({
            ...this.state,
            connected: false,
        });
    };

    updateDisplay = (readers, matchSetter) => {
        Object.keys(readers).forEach((readerName) => {
            if (this.matchDevice(readerName, readers)) {
                matchSetter(readerName);
            }
        });
    };

    matchDevice(readerName, readers) {
        const source = readers || this.state.readers;
        console.debug(
            TAG,
            'matchDevice:readers[readerName]',
            source[readerName],
        );
        const { device } = this.props;
        const reader = source[readerName];

        /* *****************************************************************************************
      Warning: this will become a bug when we add a new ucms type or change how ucms types work
      add proper querying
    ****************************************************************************************** */

        let cardType = 'unidentified';
        console.debug(TAG, 'matchDevice:reader.type:', reader.type);
        if (reader.type) {
            console.debug(
                TAG,
                'matchDevice:reader.type.includes:',
                reader.type.includes('yubikey'),
            );
            if (reader.type.includes('yubico') || reader.type.includes('yubikey')) {
                cardType = 'yubikey';
            } else if (reader.type.includes(whfbPlaceholder.subType)) {
                cardType = whfbPlaceholder.type;
            } else {
                cardType = 'smartcard';
            }
        }
        return (
            (device.type === cardType || device.subType === reader.type) &&
            (device.serial === reader.serial
                || (device.type === whfbPlaceholder.type && device.externalIdentifier === reader.serial))
        );
    }

    render() {
        const { children } = this.props;
        return (
            <DeviceDetectionWithData
                children={children}
                connected={this.state.connected}
            />
        );
    }
}

export default DeviceDetection;
